import { createContext, useContext, useEffect, useMemo } from 'react';
import { useDoc } from 'js/ydoc/doc/hooks';
import { useAssessment } from '../common/useAssesment';
import { AssessmentProvider as CommonAssessmentProvider } from 'js/ydoc/docs/common/AssessmentContext';
import { createBinding } from './binding';
import { AssessmentValidator } from './validation/validators';

import { buildCapabilitySetsFromCache } from './capability-utils';
import {
  updatePageFields,
  deleteCapabilityElement,
  moveCapabilityElementAfter,
  moveCapabilityElementBefore,
  addCapabilityElementToPage,
  togglePageCapabilitySelection,
  addPage,
  deletePage,
  movePageAfter,
  movePageBefore,
} from './actions/page-actions';

import {
  addAnswerSetChoice,
  deleteAnswerSetChoice,
  updateAnswerSetChoice,
  moveAnswerSetChoiceBefore,
  moveAnswerSetChoiceAfter,
  createAnswerSet,
  cloneAnswerSet,
  updateAnswerSet,
  deleteAnswerSet,
  assignAnswerSetToQuestion,
} from './actions/answer-set-actions';
import {
  addCapabilityElement,
  addElementToCapabilityPage,
  updateElement,
  removeElementFromCapabilityPage,
  copyAdditionalElement,
  addElementToStandardPage,
  copyStandardElement,
  deleteStandardElement,
  moveStandardElementBefore,
  moveStandardElementAfter,
  moveStandardElementToPage,
} from './actions/element-actions';

import {
  updateCacheForCapabilitySet,
  refreshCachedCapabilityPositions,
} from './actions/capability-set-actions';

import {
  updateCapabilityElement,
  removeCapabilityElement,
} from './actions/capability-actions';

export const EditorContext = createContext({
  doc: null,
  assessment: null,
  userGroupAssessment: null,
  binding: null,
  capabilitySets: null,
  cachedCapabilitySets: null,
  pageBadgeIndices: null,
  errors: null,
  updatePageFields: () => {},
  togglePageCapabilitySelection: () => {},
  deleteCapabilityElement: () => {},
  moveCapabilityElementAfter: () => {},
  moveCapabilityElementBefore: () => {},
  updateCapabilityElement: () => {},
  createAnswerSet: () => {},
  cloneAnswerSet: () => {},
  updateAnswerSet: () => {},
  deleteAnswerSet: () => {},
  addAnswerSetChoice: () => {},
  deleteAnswerSetChoice: () => {},
  updateAnswerSetChoice: () => {},
  moveAnswerSetChoiceBefore: () => {},
  moveAnswerSetChoiceAfter: () => {},
  updateCacheForCapabilitySet: () => {},
  addCapabilityElement: () => {},
  addCapabilityElementToPage: () => {},
  addElementToCapabilityPage: () => {},
  movePageAfter: () => {},
  movePageBefore: () => {},
  updateElement: () => {},
  assignAnswerSetToQuestion: () => {},
  removeElementFromCapabilityPage: () => {},
  copyAdditionalElement: () => {},
  addElementToStandardPage: () => {},
  copyStandardElement: () => {},
  deleteStandardElement: () => {},
  moveStandardElementBefore: () => {},
  moveStandardElementAfter: () => {},
  moveStandardElementToPage: () => {},
  removeCapabilityElement: () => {},
});

export function EditorProvider({
  children,
  capabilitySets,
  userGroupAssessment,
}) {
  const doc = useDoc();
  // NOTE: this can be used to delete elements from the document if needed
  // const deleteGuid = 'as-SUMQPLZ7WX';
  // doc.transact(() => {
  //   if (doc.getMap('pages').has(deleteGuid)) {
  //     doc.getMap('pages').delete(deleteGuid);
  //   }
  //   if (doc.getMap('answerSets').has(deleteGuid)) {
  //     doc.getMap('answerSets').delete(deleteGuid);
  //   }
  //   const elements = doc.getMap('elements');
  //   Object.values(elements.toJSON()).forEach((element) => {
  //     if (element.answerSetId === deleteGuid) {
  //       elements.get(element.guid).set('answerSetId', null);
  //     }
  //   });
  // });

  const { assessment, binding } = useAssessment(createBinding);

  // Keep the document's cached capability order in step with the capability
  // sets, which can be reordered after an assessment starts using them
  useEffect(() => {
    if (doc && capabilitySets && assessment?.capabilityCache) {
      refreshCachedCapabilityPositions(doc, capabilitySets);
    }
  }, [doc, capabilitySets, assessment?.capabilityCache]);
  const validator = useMemo(() => new AssessmentValidator(), []);

  const cachedCapabilitySets = useMemo(() => {
    if (assessment?.capabilitySetCache) {
      return buildCapabilitySetsFromCache(
        assessment.capabilitySetCache,
        assessment.capabilityCache
      );
    }
    return {};
  }, [assessment?.capabilitySetCache, assessment?.capabilityCache]);

  const pageBadgeIndices = useMemo(() => {
    const { lookups = {} } =
      assessment?.pages?.reduce(
        ({ lookups, current }, page) => {
          if (page.capabilitySetId && !lookups[page.capabilitySetId]) {
            lookups[page.capabilitySetId] = ++current;
          }
          return { lookups, current };
        },
        { lookups: {}, current: 0 }
      ) ?? {};
    return lookups;
  }, [assessment?.pages]);

  const actionsContext = useMemo(() => {
    return wrapActions(doc, {
      updatePageFields,
      togglePageCapabilitySelection,
      deleteCapabilityElement,
      moveCapabilityElementAfter,
      moveCapabilityElementBefore,
      updateCapabilityElement,
      createAnswerSet,
      cloneAnswerSet,
      updateAnswerSet,
      deleteAnswerSet,
      addAnswerSetChoice,
      deleteAnswerSetChoice,
      updateAnswerSetChoice,
      moveAnswerSetChoiceBefore,
      moveAnswerSetChoiceAfter,
      assignAnswerSetToQuestion,
      updateCacheForCapabilitySet,
      addCapabilityElement,
      addCapabilityElementToPage,
      addElementToCapabilityPage,
      addPage,
      deletePage,
      movePageAfter,
      movePageBefore,
      updateElement,
      removeElementFromCapabilityPage,
      copyAdditionalElement,
      addElementToStandardPage,
      copyStandardElement,
      deleteStandardElement,
      moveStandardElementBefore,
      moveStandardElementAfter,
      moveStandardElementToPage,
      removeCapabilityElement,
    });
  }, [doc]);

  const errors = useMemo(() => {
    return assessment ? validator.validateAssessment(assessment) : null;
  }, [assessment, validator]);

  const context = useMemo(() => {
    return {
      doc,
      assessment,
      userGroupAssessment,
      binding,
      capabilitySets,
      cachedCapabilitySets,
      pageBadgeIndices,
      errors,
      ...actionsContext,
    };
  }, [
    doc,
    assessment,
    userGroupAssessment,
    binding,
    capabilitySets,
    cachedCapabilitySets,
    actionsContext,
    pageBadgeIndices,
    errors,
  ]);

  return (
    <EditorContext.Provider value={context}>
      <CommonAssessmentProvider assessment={context.assessment}>
        {children}
      </CommonAssessmentProvider>
    </EditorContext.Provider>
  );
}

export function useEditorContext() {
  return useContext(EditorContext);
}

function wrapActions(doc, handlers) {
  return Object.fromEntries(
    Object.entries(handlers).map(([key, action]) => [
      key,
      (...args) => action(doc, ...args),
    ])
  );
}
